import { useEffect, useRef, useState } from 'react';
import type {
  AgentRunDTO,
  FilterState,
  JobDetailDTO,
  JobListDTO,
  RankedPropertyDTO,
  StatsDTO,
} from '@/types/db';
import { GlobalNav } from '@/components/dashboard/GlobalNav';
import { GlassPanel } from '@/components/glass/GlassPanel';
import { PropertyCard } from '@/components/dashboard/PropertyCard';
import { StatsStrip } from '@/components/dashboard/StatsStrip';
import { StatTile } from '@/components/dashboard/StatTile';
import { FilterBar } from '@/components/dashboard/FilterBar';
import { RadiusSlider } from '@/components/dashboard/RadiusSlider';
import { SortSelect } from '@/components/dashboard/SortSelect';
import { CountyMultiSelect } from '@/components/dashboard/CountyMultiSelect';
import { RecencyRadio } from '@/components/dashboard/RecencyRadio';
import { ActiveFilterChip } from '@/components/dashboard/ActiveFilterChip';
import { RankingBadge } from '@/components/dashboard/RankingBadge';
import { RankingBar } from '@/components/dashboard/RankingBar';
import { RecencyBadge } from '@/components/dashboard/RecencyBadge';
import { IncompleteDataBadge } from '@/components/dashboard/IncompleteDataBadge';
import { HeroPhoto } from '@/components/dashboard/HeroPhoto';
import { LoadingSkeleton } from '@/components/dashboard/LoadingSkeleton';
import { NearestFiveCTA } from '@/components/dashboard/NearestFiveCTA';
// Phase 07 jobs UI components
import { JobStatusBadge } from '@/components/jobs/JobStatusBadge';
import { AgentProgressGrid } from '@/components/jobs/AgentProgressGrid';
import { AgentProgressRow } from '@/components/jobs/AgentProgressRow';
import { AgentProgressList } from '@/components/jobs/AgentProgressList';
import { JobRowCard } from '@/components/jobs/JobRowCard';
import { JobRowCardSkeleton } from '@/components/jobs/JobRowCardSkeleton';
import { JobsList } from '@/components/jobs/JobsList';
import { JobDetailHeader } from '@/components/jobs/JobDetailHeader';
import { LivePollingIndicator } from '@/components/jobs/LivePollingIndicator';
import { JobErrorPanel } from '@/components/jobs/JobErrorPanel';
import { AgentProgressListSkeleton } from '@/components/jobs/AgentProgressListSkeleton';

// =====================================================================
// Mock data — shaped to match RankedPropertyDTO + StatsDTO from types/db.ts
// Plan 05-05 will wire the real /api/rankings + /api/stats responses.
// =====================================================================

const mockHigh: RankedPropertyDTO = {
  property_id: 1,
  address: '123 Coral Way, Tampa, FL 33606',
  sanitized_address: '123_coral_way_tampa',
  county: 'pinellas',
  lat: 27.9506,
  lon: -82.4572,
  created_at: new Date(Date.now() - 2 * 86_400_000).toISOString(),
  composite: 92,
  static_score: 100,
  recency_score: 100,
  walkability_score: 75,
  entertainment_score: 60,
  dog_proximity_factor: 0.92,
  amenities_in_radius_by_category: { dog_park: 3, beach: 2, daycare: 1, bar: 4 },
  is_incomplete: false,
  weights_version: 1,
};

const mockMid: RankedPropertyDTO = {
  property_id: 2,
  address: '456 Bay Street, St. Petersburg, FL 33701',
  sanitized_address: '456_bay_street_st_petersburg',
  county: 'pinellas',
  lat: 27.7676,
  lon: -82.6403,
  created_at: new Date(Date.now() - 18 * 86_400_000).toISOString(),
  composite: 68,
  static_score: 75,
  recency_score: 70,
  walkability_score: 55,
  entertainment_score: 48,
  dog_proximity_factor: 0.65,
  amenities_in_radius_by_category: { dog_park: 1, beach: 1, daycare: 0, bar: 2 },
  is_incomplete: false,
  weights_version: 1,
};

const mockIncomplete: RankedPropertyDTO = {
  property_id: 3,
  address: '789 Mango Lane, Sarasota, FL 34236',
  sanitized_address: '789_mango_lane_sarasota',
  county: 'sarasota',
  lat: 27.3364,
  lon: -82.5307,
  created_at: new Date(Date.now() - 55 * 86_400_000).toISOString(),
  composite: 41,
  static_score: 50,
  recency_score: 40,
  walkability_score: null,
  entertainment_score: 35,
  dog_proximity_factor: 0.4,
  amenities_in_radius_by_category: { dog_park: 0, beach: 1, daycare: 0, bar: 0 },
  is_incomplete: true,
  weights_version: 1,
};

const mockStats: StatsDTO = {
  total: 70,
  avg_fit: 62,
  top_fit: { id: 1, address: '123 Coral Way, Tampa, FL 33606', composite: 92 },
  freshest: {
    id: 4,
    address: '321 Mangrove Dr, Naples, FL 34102',
    created_at: new Date(Date.now() - 1 * 86_400_000).toISOString(),
  },
};

const mockColdStart: StatsDTO = {
  total: 0,
  avg_fit: null,
  top_fit: null,
  freshest: null,
};

const MOCK_COUNTIES = [
  'pinellas',
  'hillsborough',
  'sarasota',
  'manatee',
  'lee',
  'collier',
  'miami-dade',
  'broward',
  'palm-beach',
];

// =====================================================================
// Phase 07 — Jobs UI mocks
// =====================================================================

const minutesAgoIso = (m: number) =>
  new Date(Date.now() - m * 60_000).toISOString();

const mockJobPending: JobListDTO = {
  job_id: 101,
  job_name: 'cosmic-falcon',
  address: '123 Coral Way, Tampa, FL 33606',
  sanitized_address: '123_coral_way_tampa',
  property_id: 1,
  status: 'pending',
  job_type: 'submit',
  created_at: minutesAgoIso(0.1),
  started_at: null,
  completed_at: null,
  duration_sec: null,
  agent_progress: {
    walkability: 'pending',
    dog_friendliness: 'pending',
    flood_zone: 'pending',
    internet_providers: 'pending',
    entertainment: 'pending',
    property_images: 'pending',
    compiled: 'pending',
  },
};

const mockJobRunning: JobListDTO = {
  job_id: 102,
  job_name: 'dancing-monkey',
  address: '456 Bay Street, St. Petersburg, FL 33701',
  sanitized_address: '456_bay_street_st_petersburg',
  property_id: 2,
  status: 'running',
  job_type: 'submit',
  created_at: minutesAgoIso(2),
  started_at: minutesAgoIso(2),
  completed_at: null,
  duration_sec: null,
  agent_progress: {
    walkability: 'completed',
    dog_friendliness: 'completed',
    flood_zone: 'completed',
    internet_providers: 'running',
    entertainment: 'pending',
    property_images: 'pending',
    compiled: 'pending',
  },
};

const mockJobCompleted: JobListDTO = {
  job_id: 103,
  job_name: 'blazing-pretzel',
  address: '789 Mango Lane, Sarasota, FL 34236',
  sanitized_address: '789_mango_lane_sarasota',
  property_id: 3,
  status: 'completed',
  job_type: 'submit',
  created_at: minutesAgoIso(8),
  started_at: minutesAgoIso(8),
  completed_at: minutesAgoIso(2),
  duration_sec: 360,
  agent_progress: {
    walkability: 'completed',
    dog_friendliness: 'completed',
    flood_zone: 'completed',
    internet_providers: 'completed',
    entertainment: 'completed',
    property_images: 'completed',
    compiled: 'completed',
  },
};

const mockJobFailed: JobListDTO = {
  job_id: 104,
  job_name: 'red-pizza',
  address: '321 Mangrove Dr, Naples, FL 34102',
  sanitized_address: '321_mangrove_dr_naples',
  property_id: 4,
  status: 'failed',
  job_type: 'submit',
  created_at: minutesAgoIso(15),
  started_at: minutesAgoIso(15),
  completed_at: minutesAgoIso(13),
  duration_sec: 90,
  agent_progress: {
    walkability: 'completed',
    dog_friendliness: 'completed',
    flood_zone: 'failed',
    internet_providers: 'pending',
    entertainment: 'pending',
    property_images: 'pending',
    compiled: 'pending',
  },
};

const MOCK_JOBS_LIST: JobListDTO[] = [
  mockJobPending,
  mockJobRunning,
  mockJobCompleted,
  mockJobFailed,
];

const mockAgentRunsRunning: AgentRunDTO[] = [
  {
    name: 'walkability',
    status: 'completed',
    status_message: 'Found 32 amenities within 1 mile.',
    started_at: minutesAgoIso(3),
    completed_at: minutesAgoIso(2.6),
    error_message: null,
    current_step: 5,
    total_steps: 5,
    percentage: 100,
  },
  {
    name: 'dog-friendliness',
    status: 'completed',
    status_message: 'Found 4 dog-friendly venues, 1 dog park.',
    started_at: minutesAgoIso(2.5),
    completed_at: minutesAgoIso(2.0),
    error_message: null,
    current_step: 4,
    total_steps: 4,
    percentage: 100,
  },
  {
    name: 'flood-zone',
    status: 'completed',
    status_message: 'FEMA Zone X — minimal flood risk.',
    started_at: minutesAgoIso(2.0),
    completed_at: minutesAgoIso(1.5),
    error_message: null,
    current_step: 3,
    total_steps: 3,
    percentage: 100,
  },
  {
    name: 'internet-providers',
    status: 'running',
    status_message: 'Querying AllConnect for ISP coverage…',
    started_at: minutesAgoIso(0.5),
    completed_at: null,
    error_message: null,
    current_step: 2,
    total_steps: 4,
    percentage: 50,
  },
  {
    name: 'entertainment',
    status: 'pending',
    status_message: null,
    started_at: null,
    completed_at: null,
    error_message: null,
    current_step: 0,
    total_steps: 0,
    percentage: 0,
  },
  {
    name: 'property-images',
    status: 'pending',
    status_message: null,
    started_at: null,
    completed_at: null,
    error_message: null,
    current_step: 0,
    total_steps: 0,
    percentage: 0,
  },
  {
    name: 'report-compiler',
    status: 'pending',
    status_message: null,
    started_at: null,
    completed_at: null,
    error_message: null,
    current_step: 0,
    total_steps: 0,
    percentage: 0,
  },
];

const mockAgentRunsFailed: AgentRunDTO[] = [
  {
    name: 'walkability',
    status: 'completed',
    status_message: 'Found 18 amenities within 1 mile.',
    started_at: minutesAgoIso(3),
    completed_at: minutesAgoIso(2.5),
    error_message: null,
    current_step: 5,
    total_steps: 5,
    percentage: 100,
  },
  {
    name: 'dog-friendliness',
    status: 'completed',
    status_message: '2 dog-friendly bars, 0 parks within 1 mile.',
    started_at: minutesAgoIso(2.5),
    completed_at: minutesAgoIso(2.0),
    error_message: null,
    current_step: 4,
    total_steps: 4,
    percentage: 100,
  },
  {
    name: 'flood-zone',
    status: 'failed',
    status_message: 'Querying FEMA flood map service…',
    started_at: minutesAgoIso(2.0),
    completed_at: minutesAgoIso(1.8),
    error_message:
      'FEMA service returned 503 Service Unavailable; retried 3 times.',
    current_step: 1,
    total_steps: 3,
    percentage: 33,
  },
  {
    name: 'internet-providers',
    status: 'pending',
    status_message: null,
    started_at: null,
    completed_at: null,
    error_message: null,
    current_step: 0,
    total_steps: 0,
    percentage: 0,
  },
  {
    name: 'entertainment',
    status: 'pending',
    status_message: null,
    started_at: null,
    completed_at: null,
    error_message: null,
    current_step: 0,
    total_steps: 0,
    percentage: 0,
  },
  {
    name: 'property-images',
    status: 'pending',
    status_message: null,
    started_at: null,
    completed_at: null,
    error_message: null,
    current_step: 0,
    total_steps: 0,
    percentage: 0,
  },
  {
    name: 'report-compiler',
    status: 'pending',
    status_message: null,
    started_at: null,
    completed_at: null,
    error_message: null,
    current_step: 0,
    total_steps: 0,
    percentage: 0,
  },
];

const mockJobDetailRunning: JobDetailDTO = {
  job_id: 102,
  status: 'running',
  property_id: 2,
  address: '456 Bay Street, St. Petersburg, FL 33701',
  cli_running: true,
  overall_percentage: 50,
  agents: mockAgentRunsRunning,
};

const mockJobDetailFailed: JobDetailDTO = {
  job_id: 104,
  status: 'failed',
  property_id: 4,
  address: '321 Mangrove Dr, Naples, FL 34102',
  cli_running: false,
  overall_percentage: 33,
  agents: mockAgentRunsFailed,
};

// =====================================================================
// Section header helper
// =====================================================================

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="my-12">
      <h2 className="text-xl font-semibold text-foam-50 mb-1 tracking-tight">
        {title}
      </h2>
      {description && (
        <p className="text-sm text-foam-50/60 mb-4 max-w-[800px]">{description}</p>
      )}
      <div className="grid gap-6">{children}</div>
    </section>
  );
}

// =====================================================================
// Leaflet cascade probe (Plan 05-06 Pitfall 1 regression)
// Mounts a tiny Leaflet map. Leaflet's tile/zoom controls render with
// `[role=button]` markup — exactly the surface that bled Pico CSS in v1.0.
// The leaflet-pico-cascade.test.js test asserts that no Pico selectors
// appear in any /app/assets/*.css bundle when this probe is on the page.
// =====================================================================

function LeafletProbe() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let mounted = true;
    let mapInstance: { remove: () => void } | null = null;
    if (!ref.current) return;
    // Dynamic imports so SSR-style tooling never tries to evaluate Leaflet
    // at module load time. Leaflet CSS is imported here so its bundle
    // entry sits next to the React route's CSS — the Pitfall-1 regression
    // surface lives precisely where Leaflet's role=button styles meet Pico.
    void Promise.all([
      import('leaflet/dist/leaflet.css'),
      import('leaflet'),
    ]).then(([, L]) => {
      if (!mounted || !ref.current) return;
      const map = L.map(ref.current, {
        center: [28, -82],
        zoom: 7,
        zoomControl: true,
        attributionControl: false,
      });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
      }).addTo(map);
      mapInstance = map;
    });
    return () => {
      mounted = false;
      try { mapInstance?.remove(); } catch { /* ignore */ }
    };
  }, []);
  return (
    <div
      ref={ref}
      data-testid="leaflet-probe"
      className="h-64 w-full rounded-2xl overflow-hidden"
      aria-label="Leaflet cascade probe (decorative)"
    />
  );
}

// =====================================================================
// Design route
// =====================================================================

export function Design() {
  const [filters, setFilters] = useState<FilterState>({
    radius: 0.5,
    sort: 'composite',
    nearestCat: 'dog_park',
    county: ['pinellas'],
    recency: 'all',
    cats: ['parks', 'beaches', 'daycare', 'bars'],
    highlight: true,
    inRadiusOnly: false,
    showRadiusCircles: false,
    showAmenityPins: false,
  });

  return (
    <div className="min-h-screen bg-ocean-950 text-foam-50">
      <GlobalNav />
      <main id="main" className="max-w-[1180px] mx-auto px-6 py-8">
        <header className="mb-10">
          <h1 className="text-3xl font-semibold tracking-tight mb-2">
            Design System Gallery
          </h1>
          <p className="text-foam-50/70 max-w-[800px]">
            Plan 05-04 component library. One of each component from
            UI-SPEC § 3 with realistic mock props for visual inspection.
            Plan 05-05 wires these into the real `/app/` dashboard.
          </p>
        </header>

        <Section
          title="GlassPanel — base + strong"
          description="The foundational glass surface. base = surrounding (frosted, blur 12px). strong = text-bearing (lighter blur, near-solid scrim, AAA contrast)."
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <GlassPanel variant="base" className="p-6">
              <p className="text-foam-50">variant="base" (frosted)</p>
              <p className="text-sm text-foam-50/60 mt-2">
                bg-ocean-900/40 + backdrop-blur-md + foam-50/10 border
              </p>
            </GlassPanel>
            <GlassPanel variant="strong" className="p-6">
              <p className="text-foam-50">variant="strong" (text-bearing)</p>
              <p className="text-sm text-foam-50/60 mt-2">
                bg-ocean-950/72 + backdrop-blur-sm + isolation: isolate
              </p>
            </GlassPanel>
          </div>
        </Section>

        <Section
          title="StatsStrip — 5 tiles (D5)"
          description="Total · Shown · Avg Fit · Top Fit · Freshest. Top Fit + Freshest are clickable (bronze underline on hover)."
        >
          <StatsStrip stats={mockStats} shown={31} avgFit={62} />
        </Section>

        <Section
          title="StatsStrip — cold start"
          description="When stats.total === 0 the strip renders em-dashes plus a CTA subtitle on the first tile."
        >
          <StatsStrip stats={mockColdStart} shown={0} avgFit={null} />
        </Section>

        <Section
          title="StatTile — clickable variant"
          description="Single tile in isolation. Hover to see the bronze underline reserved for clickable tiles."
        >
          <div className="max-w-xs">
            <StatTile
              label="Top Fit"
              value="123 Coral Way · 92"
              clickable
              href="/properties/1"
            />
          </div>
        </Section>

        <Section
          title="FilterBar — composed control surface"
          description="Sticky in the real dashboard; inline here for inspection. State is local to the gallery; FilterBar is fully controlled by useFilters in Plan 05-05."
        >
          <FilterBar
            filters={filters}
            setFilters={setFilters}
            resultCount={31}
            totalCount={70}
            availableCounties={MOCK_COUNTIES}
          />
          <div className="flex flex-wrap gap-2">
            <ActiveFilterChip
              label={`Radius ${filters.radius.toFixed(1)} mi`}
              onRemove={() => setFilters({ ...filters, radius: 0.5 })}
            />
            {filters.county.map((slug) => (
              <ActiveFilterChip
                key={slug}
                label={slug.charAt(0).toUpperCase() + slug.slice(1)}
                onRemove={() =>
                  setFilters({
                    ...filters,
                    county: filters.county.filter((s) => s !== slug),
                  })
                }
              />
            ))}
            {filters.recency !== 'all' && (
              <ActiveFilterChip
                label={`Last ${filters.recency}`}
                onRemove={() => setFilters({ ...filters, recency: 'all' })}
              />
            )}
          </div>
        </Section>

        <Section title="RadiusSlider — standalone">
          <div className="max-w-md">
            <RadiusSlider
              value={filters.radius}
              onChange={(v) => setFilters({ ...filters, radius: v })}
            />
          </div>
        </Section>

        <Section title="SortSelect, CountyMultiSelect, RecencyRadio — standalone">
          <div className="flex flex-wrap items-center gap-4">
            <SortSelect
              value={{ sort: filters.sort, nearestCat: filters.nearestCat }}
              onChange={(next) =>
                setFilters({ ...filters, sort: next.sort, nearestCat: next.nearestCat })
              }
            />
            <CountyMultiSelect
              value={filters.county}
              availableCounties={MOCK_COUNTIES}
              onChange={(v) => setFilters({ ...filters, county: v })}
            />
            <RecencyRadio
              value={filters.recency}
              onChange={(v) => setFilters({ ...filters, recency: v })}
            />
          </div>
        </Section>

        <Section
          title="RankingBadge — score states"
          description="≥80 → bronze numeral. 50–79 → foam. <50 → faded foam. is_incomplete → coral asterisk + coral border."
        >
          <div className="flex flex-wrap gap-6 items-center">
            <RankingBadge score={92} dogProximityFactor={0.92} />
            <RankingBadge score={68} dogProximityFactor={0.65} />
            <RankingBadge score={41} dogProximityFactor={0.4} />
            <RankingBadge score={41} dogProximityFactor={0.4} isIncomplete />
          </div>
        </Section>

        <Section
          title="RankingBar — three axes, with empty state"
          description="dog → tide-400. walk_ent → moss-700. fresh → foam-50/40. Empty score (null) renders strikethrough label and em-dash."
        >
          <div className="max-w-[640px] grid gap-2 p-6 glass-strong">
            <RankingBar
              axis="dog"
              score={88}
              tooltip="3 dog parks, 2 dog beaches, 1 daycare, 4 dog-friendly bars within 0.5 mi"
              animate
            />
            <RankingBar
              axis="walk_ent"
              score={72}
              tooltip="Walkability: 75/100 · Entertainment: 60/100"
              animate
            />
            <RankingBar
              axis="fresh"
              score={100}
              tooltip="Listed 2 days ago — bucket: new"
              animate
            />
            <RankingBar
              axis="walk_ent"
              score={null}
              tooltip="No walkability data yet"
              animate={false}
            />
          </div>
        </Section>

        <Section
          title="RecencyBadge — 4 tiers"
          description="≤7d gets the bronze 'New' pill (one of the 6 reserved-for-list places). 8-30d, 31-90d, >90d use de-emphasised foam tones."
        >
          <div className="flex flex-wrap gap-3">
            <RecencyBadge daysSince={2} />
            <RecencyBadge daysSince={12} />
            <RecencyBadge daysSince={45} />
            <RecencyBadge daysSince={120} />
          </div>
        </Section>

        <Section
          title="IncompleteDataBadge"
          description="Coral pill with AlertCircle icon. Hover for renormalisation explanation."
        >
          <IncompleteDataBadge />
        </Section>

        <Section
          title="HeroPhoto — fallback"
          description="src=null triggers the bg-ocean-800 + Home icon placeholder used when an image is missing or fails to load."
        >
          <div className="w-[440px] h-[330px] overflow-hidden rounded-2xl">
            <HeroPhoto src={null} alt="123 Coral Way, Tampa, FL" />
          </div>
        </Section>

        <Section
          title="PropertyCard — 3 states"
          description="High-fit (≥80, bronze numeral) · mid-fit · incomplete data."
        >
          <PropertyCard property={mockHigh} radius={filters.radius} priority />
          <PropertyCard property={mockMid} radius={filters.radius} />
          <PropertyCard property={mockIncomplete} radius={filters.radius} />
        </Section>

        <Section
          title="LoadingSkeleton — 3 variants"
          description="card · tile · row. animate-pulse (honours prefers-reduced-motion)."
        >
          <LoadingSkeleton variant="card" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <LoadingSkeleton variant="tile" />
            <LoadingSkeleton variant="tile" />
            <LoadingSkeleton variant="tile" />
          </div>
          <LoadingSkeleton variant="row" />
        </Section>

        <Section
          title="Leaflet Cascade Probe (Pitfall 1 regression surface)"
          description="Mounts a Leaflet map under /app/. Leaflet's controls render with [role=button] — the exact surface that bled Pico CSS in v1.0. The Plan 05-06 leaflet-pico-cascade.test.js asserts no Pico selectors appear in /app/assets/*.css when this probe is on the page."
        >
          <LeafletProbe />
        </Section>

        <Section
          title="NearestFiveCTA — empty filter state"
          description="Renders when filtered count = 0. The bronze button is the 5th of 6 places bronze appears."
        >
          <NearestFiveCTA
            onShowNearestFive={() => {
              console.log('Show nearest 5 properties clicked');
            }}
          />
        </Section>

        {/* ================================================================
         *  Phase 07 — Jobs UI components (Plan 07-02)
         *  Plan 07-03 mounts these into /app/jobs and /app/jobs/:id routes;
         *  this section renders one of each with mock data so visual
         *  inspection works without spawning a real Claude CLI job.
         *  ================================================================ */}

        <Section
          title="JobStatusBadge — 4 variants × 2 sizes"
          description="Pending (muted) · Running (tide-400, animate-pulse) · Completed (moss-500) · Failed (coral-500). sm = list rows, md = detail header band."
        >
          <div className="flex flex-wrap items-center gap-3">
            <JobStatusBadge status="pending" size="sm" />
            <JobStatusBadge status="running" size="sm" />
            <JobStatusBadge status="completed" size="sm" />
            <JobStatusBadge status="failed" size="sm" />
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-2">
            <JobStatusBadge status="pending" size="md" />
            <JobStatusBadge status="running" size="md" />
            <JobStatusBadge status="completed" size="md" />
            <JobStatusBadge status="failed" size="md" />
          </div>
        </Section>

        <Section
          title="AgentProgressGrid — 7-dot mini grid"
          description="Locked execution order. Hover a dot for tooltip. Aggregate aria-label communicates progress to SR users."
        >
          <div className="flex flex-col gap-3 p-4 glass-base">
            <div className="flex items-center gap-3">
              <span className="text-sm text-foam-50/70 w-32">All pending</span>
              <AgentProgressGrid agentProgress={mockJobPending.agent_progress} />
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-foam-50/70 w-32">Mid-run</span>
              <AgentProgressGrid agentProgress={mockJobRunning.agent_progress} />
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-foam-50/70 w-32">All complete</span>
              <AgentProgressGrid agentProgress={mockJobCompleted.agent_progress} />
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-foam-50/70 w-32">Failed</span>
              <AgentProgressGrid agentProgress={mockJobFailed.agent_progress} />
            </div>
          </div>
        </Section>

        <Section
          title="LivePollingIndicator — polling on/off"
          description="Pulsing tide-400 dot + 'Updating live'. Fades out via 200ms opacity on isPolling=false."
        >
          <div className="flex flex-col gap-3 p-4 glass-strong">
            <LivePollingIndicator isPolling={true} />
            <LivePollingIndicator
              isPolling={false}
              finalStatus="completed"
            />
          </div>
        </Section>

        <Section
          title="JobRowCard — 4 status variants"
          description="Full-card link. Hover lifts -translate-y-0.5 + shadow. Aria-label aggregates status + completed-count."
        >
          <div className="flex flex-col gap-4">
            <JobRowCard job={mockJobPending} />
            <JobRowCard job={mockJobRunning} />
            <JobRowCard job={mockJobCompleted} />
            <JobRowCard job={mockJobFailed} />
          </div>
        </Section>

        <Section
          title="JobRowCardSkeleton — loading-state"
          description="animate-pulse blocks matching JobRowCard anatomy."
        >
          <div className="flex flex-col gap-4">
            <JobRowCardSkeleton />
            <JobRowCardSkeleton />
          </div>
        </Section>

        <Section
          title="JobsList — 5 states"
          description="error · loading · cold-start empty · search filtered to 0 · populated. Each state below is a controlled render."
        >
          <h3 className="text-sm font-semibold text-foam-50/80">Populated</h3>
          <JobsList
            jobs={MOCK_JOBS_LIST}
            isLoading={false}
            isError={false}
            searchQuery=""
            onSearchChange={() => {}}
          />
          <h3 className="text-sm font-semibold text-foam-50/80 mt-4">Loading</h3>
          <JobsList
            jobs={undefined}
            isLoading={true}
            isError={false}
            searchQuery=""
            onSearchChange={() => {}}
          />
          <h3 className="text-sm font-semibold text-foam-50/80 mt-4">Error</h3>
          <JobsList
            jobs={undefined}
            isLoading={false}
            isError={true}
            onRetry={() => {}}
            searchQuery=""
            onSearchChange={() => {}}
          />
          <h3 className="text-sm font-semibold text-foam-50/80 mt-4">
            Cold-start empty
          </h3>
          <JobsList
            jobs={[]}
            isLoading={false}
            isError={false}
            searchQuery=""
            onSearchChange={() => {}}
          />
          <h3 className="text-sm font-semibold text-foam-50/80 mt-4">
            Search filtered to 0
          </h3>
          <JobsList
            jobs={MOCK_JOBS_LIST}
            isLoading={false}
            isError={false}
            searchQuery="zzz-no-match"
            onSearchChange={() => {}}
          />
        </Section>

        <Section
          title="AgentProgressRow — single rows"
          description="Full-row md+ 5-col grid / mobile 2-row stack. Failed row surfaces error_message in coral."
        >
          <GlassPanel variant="base" className="p-4 md:p-6">
            <div role="list" className="flex flex-col">
              {mockAgentRunsRunning.slice(0, 4).map((a) => (
                <AgentProgressRow key={a.name} agentRun={a} />
              ))}
              <AgentProgressRow agentRun={mockAgentRunsFailed[2]!} />
            </div>
          </GlassPanel>
        </Section>

        <Section
          title="AgentProgressList — full 7 rows (running)"
          description="Live elapsed counter ticks every 1s for the running row. Locked execution order."
        >
          <AgentProgressList agentRuns={mockAgentRunsRunning} />
        </Section>

        <Section
          title="AgentProgressListSkeleton"
          description="7 placeholder rows; aria-busy on the wrapper communicates loading."
        >
          <AgentProgressListSkeleton />
        </Section>

        <Section
          title="JobDetailHeader — running"
          description="Address h1 + meta + LivePollingIndicator + status badge (md size). isPolling=true."
        >
          <JobDetailHeader
            job={mockJobDetailRunning}
            jobName={mockJobRunning.job_name}
            startedAt={mockJobRunning.started_at}
            durationSec={null}
            isPolling={true}
          />
        </Section>

        <Section
          title="JobDetailHeader — completed (with View Property link)"
          description="Polling stopped. Completed jobs surface a 'View Property →' link to /app/properties/:id."
        >
          <JobDetailHeader
            job={{
              ...mockJobDetailRunning,
              status: 'completed',
              overall_percentage: 100,
            }}
            jobName={mockJobCompleted.job_name}
            startedAt={mockJobCompleted.started_at}
            durationSec={mockJobCompleted.duration_sec}
            isPolling={false}
          />
        </Section>

        <Section
          title="JobErrorPanel — failed-job action"
          description="Coral-bordered glass-strong panel with Resubmit link. role='alert' so SR users hear it on appearance."
        >
          <JobErrorPanel job={mockJobDetailFailed} />
        </Section>
      </main>
    </div>
  );
}
